import * as THREE from "three";
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial'
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry";
import { Wireframe } from "three/examples/jsm/lines/Wireframe";
import { Dist } from "./Dist";
import { AMember, appdata, memberKind } from './internalModules'
import { styled } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";



export default class Graphics {
  static backgroundColor: THREE.Color = new THREE.Color(0xf4f4f4)
  static lineMaterial: LineMaterial = new LineMaterial({color: 0x000000, linewidth: 0.0030})
  static explodeAmount = 4
  static explodeFillAmount = 0.3 * Graphics.explodeAmount

  static getNewScene(): THREE.Scene {
    const scene = new THREE.Scene()
    scene.background = Graphics.backgroundColor
    scene.add(Graphics.getNewAmbientLight())
    scene.add(Graphics.getNewDirectionalLight())
    //scene.add(Graphics.getNewAxisHelper())
    return scene
  }

  static getNewOrthoCamera(): THREE.OrthographicCamera {
    const zoom = 25 
    const camera = new THREE.OrthographicCamera(-zoom, zoom, zoom, -zoom, 0.1, 1000)
    camera.position.x = 7/10 * zoom
    camera.position.y = 7/10 * zoom
    camera.position.z = zoom
    //camera.name = 'ortho camera'
    return camera
  }

  static getNewPerspectiveCamera(): THREE.PerspectiveCamera {
    const camera = new THREE.PerspectiveCamera( 20, window.innerWidth/window.innerHeight, 0.1, 1000 )
    camera.position.z = 60
    camera.position.x = 20
    camera.position.y = 20
    camera.lookAt(0,0,0)
    //camera.name = 'perspective camera'
    return camera
  }

  static getNewPerspectiveCameraHorizAssembly(): THREE.PerspectiveCamera {
    const camera = new THREE.PerspectiveCamera( 20, window.innerWidth/window.innerHeight, 0.1, 1000 )
    camera.position.z = 100
    camera.position.x = 50
    camera.position.y = 50
    camera.lookAt(0,0,0)
    //camera.name = 'perspective camera'
    return camera
  }

  static getNewDirectionalLight(): THREE.DirectionalLight {
    const light = new THREE.DirectionalLight()
    //light.name = 'directional light'
    light.position.x = -1
    light.position.y = .5
    light.position.z = 1
    light.intensity = 1.0
    return light
  }

  static getNewAmbientLight(): THREE.AmbientLight {
    const light = new THREE.AmbientLight(undefined, 0.7)
    //light.name = 'ambient light'
    return light
  }

  static getNewAxisHelper(): THREE.AxesHelper  {
    const helper = new THREE.AxesHelper(7)
    //helper.name = 'axis helper'
    return helper
  }

  static getMemberSolidShape(depth: Dist, width: Dist): THREE.Shape {
    const d = depth.toFeet()
    const w = width.toFeet()
    const points = [
      new THREE.Vector2(0, 0),
      new THREE.Vector2(w, 0),
      new THREE.Vector2(w, d),
      new THREE.Vector2(0, d),
      new THREE.Vector2(0, 0),
    ]
    return new THREE.Shape(points)
  }

  static getMemberHollowShape(depth: Dist, width: Dist, thickness: Dist): THREE.Shape {
    const d = depth.toFeet()
    const w = width.toFeet()
    const t = thickness.toFeet()
    // make outer points
    const points = [
      new THREE.Vector2(0, 0),
      new THREE.Vector2(w, 0),
      new THREE.Vector2(w, d),
      new THREE.Vector2(0, d),
      new THREE.Vector2(0, 0),
    ]
    
    //make inner points
    const holePoints = [
      new THREE.Vector2(t, t),
      new THREE.Vector2(w-t, t),
      new THREE.Vector2(w-t, d-t),
      new THREE.Vector2(t, d-t),
      new THREE.Vector2(t, t),
    ]
    const shape = new THREE.Shape(points)
    shape.holes = [new THREE.Path(holePoints)]
    return shape
  }

  static getMemberMetalDeckShape(depth: Dist, width: Dist, thickness: Dist, topFlute: Dist, bottomFlute: Dist): THREE.Shape {

    const points = this.getMemberMetalDeckShapePoints(depth, width, topFlute, bottomFlute)
    const thickVector = new THREE.Vector2(0, thickness.ft)
    for (const p of Array.from(points).reverse()) {
      points.push(p.add(thickVector))
    }
    return new THREE.Shape(points)
  }

  static getMemberMetalDeckShapePoints(depth: Dist, width: Dist, topFlute: Dist, bottomFlute: Dist): THREE.Vector2[] {
    const d = depth.ft
    const w = width.ft
    const tf = topFlute.ft
    const bf = bottomFlute.ft
    const slopeH = (w - bf - tf) / 2

    const points = [
      new THREE.Vector2(0, 0),
      new THREE.Vector2(slopeH, d),
      new THREE.Vector2(slopeH + tf, d),
      new THREE.Vector2(slopeH + tf + slopeH, 0),
      new THREE.Vector2(w, 0)
    ]
    return points
  }

  static getLayerMetalDeckPoints(member: AMember, moduleSize: Dist): THREE.Vector2[] {
    let points: THREE.Vector2[] = [new THREE.Vector2(0,0), new THREE.Vector2(0,1)]
    if(member.kind === memberKind.metalDeck){
      points = []
      const shapePoints = this.getMemberMetalDeckShapePoints(member.depth, member.width, member.topFlute, member.bottomFlute)
      const initialBottomFlute = member.bottomFlute.ft/2
      points.push(new THREE.Vector2(0,0)) //add first point at origin
      points.push(new THREE.Vector2(initialBottomFlute, 0)) //always start with half a bottom flute
      let xPosition = initialBottomFlute
      let ptIndex = 0
      let i = 0
      while (xPosition < moduleSize.ft) {
        i = ptIndex
        if(i > shapePoints.length-1){
          i = ptIndex % shapePoints.length
        }
        if(i === 0){
          ptIndex ++
          continue //skip initial point of shape because it is the same as last point
        }
        const shapeIterations = Math.floor(ptIndex/shapePoints.length)
        const newPoint = new THREE.Vector2(shapePoints[i].x, shapePoints[i].y)
        newPoint.add(new THREE.Vector2(shapeIterations*member.width.ft + initialBottomFlute, 0))
        points.push(newPoint)
        xPosition += shapePoints[i].x - shapePoints[i-1].x
        if(xPosition > moduleSize.ft){
          //last point overshot, move it back to module size
          points[points.length-1].setX(moduleSize.ft)
        }
        ptIndex ++
      }
      // add one final point at the moduleSize using the same y value as the last added point
      const finalPt = new THREE.Vector2(moduleSize.ft, points[points.length-1].y)
      points.push(finalPt)
    }
    return points
  }

  static getLayerMetalDeckShape(member: AMember, moduleSize: Dist): THREE.Shape {
    let points = this.getLayerMetalDeckPoints(member, moduleSize)
    if(member.kind === memberKind.metalDeck){
      for (const p of Array.from(points).reverse()) {
        points.push(new THREE.Vector2(p.x, p.y + member.thickness.ft))
      }
    }
    return new THREE.Shape(points)
  }

  static getLayerMetalDeckInfillShape(member: AMember, fillThickness: Dist, moduleSize: Dist): THREE.Shape{
    let points = this.getLayerMetalDeckPoints(member, moduleSize)
    //add points for top surface of concrete
    const pointY = member.depth.ft + fillThickness.ft
    points.push(new THREE.Vector2(points[points.length-1].x, pointY))
    points.push(new THREE.Vector2(points[0].x, pointY))
    return new THREE.Shape(points)
  }

  static getMemberColdFormedShape(depth: Dist, width: Dist, thickness: Dist, lip: Dist): THREE.Shape {
    const d = depth.toFeet()
    const w = width.toFeet()
    const t = thickness.toFeet()
    const l = lip.toFeet()
    const points = [
      new THREE.Vector2(0,0),
      new THREE.Vector2(w,0),
      new THREE.Vector2(w,l),
      new THREE.Vector2(w-t,l),
      new THREE.Vector2(w-t,t),
      new THREE.Vector2(t,t),
      new THREE.Vector2(t,d-t),
      new THREE.Vector2(w-t,d-t),
      new THREE.Vector2(w-t,d-l),
      new THREE.Vector2(w,d-l),
      new THREE.Vector2(w,d),
      new THREE.Vector2(0,d),
      new THREE.Vector2(0,0),
    ]
    return new THREE.Shape(points)
  }

  static getMemberWideFlangeShape(depth: Dist, width: Dist, tWeb: Dist, tFlange: Dist): THREE.Shape {
    const d = depth.ft
    const w = width.ft
    const tw = tWeb.ft
    const tf = tFlange.ft
    const points = [
      new THREE.Vector2(0,0),
      new THREE.Vector2(w,0),
      new THREE.Vector2(w,tf),
      new THREE.Vector2((w+tw)/2,tf),
      new THREE.Vector2((w+tw)/2,d-tf),
      new THREE.Vector2(w,d-tf),
      new THREE.Vector2(w,d),
      new THREE.Vector2(0,d),
      new THREE.Vector2(0,d-tf),
      new THREE.Vector2((w-tw)/2,d-tf),
      new THREE.Vector2((w-tw)/2,tf),
      new THREE.Vector2(0,tf),
      new THREE.Vector2(0,0),
    ]
    return new THREE.Shape(points)
  }

  static getCylinderPoints(diameter: Dist): number {
    const dia = diameter.in
    let pointCount = 5
    if(dia > 1){
      pointCount = 6
    }
    if(dia > 3){
      pointCount = 8
    }
    if(dia > 6){
      pointCount = 10
    }
    return pointCount
  }

  static getMemberRoundShape(diameter: Dist):THREE.Shape {
    const d = diameter.ft
    const circle = new THREE.EllipseCurve(d/2,d/2, d/2, d/2, 0, 2*Math.PI, true, 0)
    const points = circle.getPoints(this.getCylinderPoints(diameter))
    return new THREE.Shape(points)
  }

  static getMemberRoundHollowShape(diameter: Dist, wallThickness: Dist): THREE.Shape {
    const d = diameter.ft
    const tw = wallThickness.ft
    const pointCount = this.getCylinderPoints(diameter)
    const outerCircle = new THREE.EllipseCurve(d/2,d/2, d/2, d/2, 0, 2*Math.PI, true, 0)
    const outerCirclePoints = outerCircle.getPoints(pointCount)
    const innerCircle = new THREE.EllipseCurve(d/2,d/2, d/2-tw, d/2-tw, 0, 2*Math.PI, true, 0)
    const innerCirclePoints = innerCircle.getPoints(pointCount)
    const shape = new THREE.Shape(outerCirclePoints)
    shape.holes = [new THREE.Path(innerCirclePoints)]
    return shape
  }

  static getMemberZeeShape(depth: Dist, width: Dist, thickness: Dist): THREE.Shape {
    const d = depth.ft
    const w = width.ft
    const t = thickness.ft
    const points = [
      new THREE.Vector2(0,d),
      new THREE.Vector2(w/2+t/2, d),
      new THREE.Vector2(w/2+t/2, t),
      new THREE.Vector2(w, t),
      new THREE.Vector2(w, 0),
      new THREE.Vector2(w/2-t/2, 0),
      new THREE.Vector2(w/2-t/2, d-t),
      new THREE.Vector2(0, d-t),
    ]
    return new THREE.Shape(points)
  }

  static clearSceneMeshes(scene: THREE.Scene) {
    scene.clear()
    scene.add(Graphics.getNewAmbientLight())
    scene.add(Graphics.getNewDirectionalLight())
    //scene.add(Graphics.getNewAxisHelper())
  }

  static getMaterial (opacity: number): THREE.MeshPhongMaterial {
    const isTransparent = opacity < 1
    return new THREE.MeshPhongMaterial({opacity: opacity, transparent: isTransparent})
  }

  static edgeMesh (geometry: THREE.BufferGeometry, opacity = 1): THREE.Mesh {
    const edgesGeometry = new THREE.EdgesGeometry(geometry)
    const lineGeometry = new LineSegmentsGeometry().fromEdgesGeometry(edgesGeometry)
    const wireframe = new Wireframe(lineGeometry, Graphics.lineMaterial)
    const mesh = new THREE.Mesh(geometry, Graphics.getMaterial(opacity))
    mesh.add(wireframe)
    return mesh
  }

  /* static edgeMeshTransparent (geometry: THREE.BufferGeometry): THREE.Mesh {
    const edgesGeometry = new THREE.EdgesGeometry(geometry)
    const lineGeometry = new LineSegmentsGeometry().fromEdgesGeometry(edgesGeometry)
    const wireframe = new Wireframe(lineGeometry, Graphics.lineMaterial)
    const mesh = new THREE.Mesh(geometry, Graphics.transparentMaterial)
    mesh.add(wireframe)
    return mesh
  } */

  // returns a color palette based on the input strings
  // this function written by generative AI (Claude)
  static generateColorsFromStrings(strings: string[]): string[] {
    // Base color to generate variations from
    const BASE_COLOR = '#107dac'
  
    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string): number[] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      if (!result) {
        throw new Error(`Invalid hex color: ${hex}`)
      }
      return [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
    }
  
    // Helper function to convert RGB to hex
    const rgbToHex = (r: number, g: number, b: number): string => {
      const toHex = (n: number): string => {
        const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`
    }
  
    // Generate a deterministic hash from a string
    const hashString = (str: string): number => {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32-bit integer
      }
      return hash
    }
  
    // Generate a color variation based on a hash
    const generateVariation = (baseRgb: number[], hash: number): string => {
      // Use different parts of the hash for each RGB component
      const shift = ((hash & 0xFF) - 128) /2
  
      // Apply the shifts to the base color
      const newR = Math.max(0, Math.min(255, baseRgb[0] + shift))
      const newG = Math.max(0, Math.min(255, baseRgb[1] + shift))
      const newB = Math.max(0, Math.min(255, baseRgb[2] + shift))
  
      return rgbToHex(newR, newG, newB)
    }
  
    // Convert base color to RGB
    const baseRgb = hexToRgb(BASE_COLOR);
  
    // Generate colors for each input string
    return strings.map(str => {
      const hash = hashString(str);
      return generateVariation(baseRgb, hash);
    });
  }

  // this function returns an array of color strings from session storage if they match the quantity of
  // assemblies or generates new colors
  static getAssemblyColors(): string[] {
    let colors: string[] = []
    const savedColors = sessionStorage.getItem('assemblyColors')
    if (savedColors && savedColors.length === appdata.getWallTypeArray().length) {
      colors = JSON.parse(sessionStorage.getItem('assemblyColors') as string)
    }
    else {
      colors = this.generateColorsFromStrings(appdata.getWallTypeArray().map(wt => wt.guid))
      sessionStorage.setItem('assemblyColors', JSON.stringify(colors))
    }
    return colors
  }
  
  // returns a material to color geometry based on carbon intensity
  static getMaterialColorScaled (currentMaterial: THREE.MeshPhongMaterial, ratio: number): THREE.MeshPhongMaterial {
    const opacity = currentMaterial.opacity
    const transparent = currentMaterial.transparent
    const color = this.getColorByRange3(ratio)
    return new THREE.MeshPhongMaterial({color, opacity, transparent})
  }

  static getMaterialColorCategory (currentMaterial: THREE.MeshPhongMaterial, category: string): THREE.MeshPhongMaterial {
    const opacity = currentMaterial.opacity
    const transparent = currentMaterial.transparent
    const color = mapCategoryColors.get(category)!
    return new THREE.MeshPhongMaterial({color, opacity, transparent})
  }

  static getColorByRange (value: number): THREE.Color {
    let index = 0
    switch (true) {
      case (value > .7):
        index = 9
        break
      case (value > .5):
        index = 8
        break
      case (value > .35):
        index = 7
        break
      case (value > .2):
        index = 6
        break
      case (value > .10):
        index = 5
        break
      case (value > .05):
        index = 4
        break
      case (value > .025):
        index = 3
        break
      case (value > .0125):
        index = 2
        break
      case (value > .006):
        index = 1
        break 
    }

    return colorScale[index]
  }

  static getColorByRange2 (value: number): THREE.Color {
    let index = 3
    switch (true) {
      case (value > .4):
        index = 9
        break
      case (value > .3):
        index = 6
        break
      case (value > .15):
        index = 4
        break
    }
    return colorScale[index]
  }

  static getColorByRange3 (value: number): THREE.Color {
    let index = 3
    switch (true) {
      case (value > 0.4):
        index = 0
        break
      case (value > 0.2):
        index = 1
        break
      case (value > 0.1):
        index = 2
        break
    }
    return colorScale2[index]
  }

  static getColorStringByRange (value: number): string {
    let index = 3
    switch (true) {
      case (value > 0.4):
        index = 0
        break
      case (value > 0.2):
        index = 1
        break
      case (value > 0.1):
        index = 2
        break
    }
    return colorScaleStrings[index]
  }

}

//global variable for THREE Renderer
export var globalRenderer: THREE.WebGLRenderer = new THREE.WebGLRenderer({alpha: true, antialias: true})

//color scale from lowest percent carbon to highest
export const colorScale: THREE.Color[] = [
  new THREE.Color("rgb(255, 254, 233)"),
  new THREE.Color("rgb(255, 250, 124)"),
  new THREE.Color("rgb(254, 238, 81)"),
  new THREE.Color("rgb(255, 214, 114)"),
  new THREE.Color("rgb(255, 197, 179)"),
  new THREE.Color("rgb(255, 184, 233)"),
  new THREE.Color("rgb(217, 155, 250)"),
  new THREE.Color("rgb(175, 126, 245)"),
  new THREE.Color("rgb(130, 100, 204)"),
  new THREE.Color("rgb(90, 76, 143)")
]

export const colorScale2: THREE.Color[] = [
  new THREE.Color( 0x005073 ),
  new THREE.Color( 0x107dac ),
  new THREE.Color( 0x189ad3 ),
  new THREE.Color( 0x71c7ec ),
  //new THREE.Color( 0x1ebbd7 ),
  //new THREE.Color( 0x71c7ec )
]

export const colorScaleStrings: string[] = [
  '#005073',
  '#107dac',
  '#189ad3',
  '#71c7ec']

export const colorScale3: THREE.Color[] = [
  new THREE.Color( 0x002B46 ),
  new THREE.Color( 0x104B6F ),
  new THREE.Color( 0x185E8B ),
  new THREE.Color( 0x719FBC )
]

export const mapCategoryColors: Map<string, THREE.Color> = new Map([
  ['00 Other', new THREE.Color(0xffffff)],
  ['03 Concrete', new THREE.Color(0x707070)],
  ['04 Masonry', new THREE.Color(0xe77878)],
  ['05 Metals', new THREE.Color(0x850000)],
  ['06 Wood & Plastic', new THREE.Color(0xC49F6F)],
  ['07 Thermal & Moisture Protection', new THREE.Color(0xacdc98)],
  ['08 Openings', new THREE.Color(0xb9b9b9)],
  ['09 Finishes', new THREE.Color(0xffffff)],
])

// from https://github.com/mrdoob/three.js/pull/14526
export function updateCameraToScene(camera: THREE.PerspectiveCamera, scene: THREE.Scene, controls: OrbitControls|undefined, fitRatio = 1.5) {
  if(!controls){
    return
  }
  const box = new THREE.Box3()
  if(scene.children.length >= 3){
    for(const object of scene.children){
      box.expandByObject(object)
    }
  }

  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())

  const maxSize = Math.max(size.x, size.y, size.z)
  const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360))
  const fitWidthDistance = fitHeightDistance / camera.aspect
  const distance = fitRatio * Math.max(fitHeightDistance, fitWidthDistance)

  camera.lookAt(center)

  const direction = controls.target
    .clone()
    .sub(camera.position)
    .normalize()
    .multiplyScalar(distance)

  controls.maxDistance = distance * 10
  controls.target.copy(center)

  camera.near = distance / 100
  camera.far = distance * 100
  camera.updateProjectionMatrix()

  camera.position.copy(controls.target).sub(direction)
  controls.update()
}

// UI graphics constants
export const rowHeight = 36
export const boxStyle = { flexGrow: 1, paddingLeft: 4, paddingRight:4, paddingBottom:2 }
export const textFieldStyle = { height: rowHeight, width: '100%'}
export const halfWidthStyle = { height: rowHeight, width: '50%'}
export const alertStyle = { mx: 4, marginBottom: 1 }
export const dialogTypographyStyle = {mt: 2}
export const dialogPaperProps = { sx: { marginTop: '2%', verticalAlign: "top" } }
export const drawerWidth = 800
export const drawerButtonStyle = { my:1, mx:15, width: drawerWidth-250}
export const drawerTableStyle = { height: 'calc(100% - 150px)', width: '100%' }
export const welcomeButtonStyle = { m: 0.25, px: 4}
export const appBarButtonStyle = { m: 0.25, px: 1}

export const StyledDataGrid = styled(DataGrid)`
  &.MuiDataGrid-root .MuiDataGrid-columnHeader:focus,
  &.MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
  },
  &.MuiDataGrid-root:hover {
    cursor: pointer;
  },
`